/**
 * Comment endpoints #16/#17 of the frozen contract (docs/05-dev-spec.md §4) —
 * the venue-card thread of task T.C4a.
 *
 *   #17  GET  /api/comments?venueId=…   VIEWER   `{data:{comments:[CommentDto]}, meta}`
 *   #16  POST /api/comments             MANAGER  `{ venueId | pguId, body }` → `{data: CommentDto}`
 *
 * Shapes mirror the generated `CommentDto` / `CommentCreateDto` /
 * `CommentListEnvelope` (`packages/shared/src/openapi/api-types.ts`); the same
 * barrel-gap rule as `./venues` keeps them declared here.
 *
 * The XOR invariant is the api's to enforce (exactly one of `venueId`/`pguId`,
 * else 400 VALIDATION): this module only ever sends `venueId`, so a venue
 * thread cannot trip it.
 *
 * T.Z66 — a comment scope is VENUE-LEVEL on this surface and both halves of the
 * thread agree on it: #16 writes `{venueId, body}` and #17 reads the same
 * `venueId`, so a persisted row is listable by construction. What did NOT agree
 * was the READ UNWRAP: it expected the pre-v1.1 bare array, so the real api's
 * `{data:{comments:[…]}, meta}` (the shape the contract corrected to in v1.1)
 * degraded to an empty thread — the optimistic row vanished on the refetch and
 * nothing survived a reload, while the row sat in Postgres all along.
 * {@link commentsOf} now reads the ratified nested shape (and still accepts a
 * bare array, which the Playwright stubs and older clients speak).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { buildQuery, requestEnvelope } from './client';
import { commentsOf } from './comments-model';

/** Mirrors `CommentDto`. `authorName` is the RESOLVED display name (server-side). */
export interface CommentDto {
  id: number;
  venueId: number | null;
  pguId: string | null;
  authorUserId: number;
  authorName: string | null;
  body: string;
  /** ISO-8601. */
  createdAt: string;
}

/** #16 body, venue-flavoured — the venue card never writes an application comment. */
export interface VenueCommentCreate {
  venueId: number;
  body: string;
}

export function venueCommentsPath(venueId: number): string {
  return `/comments${buildQuery({ venueId })}`;
}

/**
 * #17 read: `unwrapEnvelope` lifts the `data` key, `commentsOf` (the pure,
 * unit-tested half in `./comments-model`) then reads the nested
 * `{comments:[…]}` list the contract answers — the step that used to coerce the
 * whole thread to `[]` (T.Z66).
 */
export async function fetchVenueComments(venueId: number, signal?: AbortSignal): Promise<CommentDto[]> {
  const envelope = await requestEnvelope<unknown>('GET', venueCommentsPath(venueId), ['data'], { signal });
  return commentsOf(envelope.data);
}

export function venueCommentsQueryKey(venueId: number): readonly [string, { venueId: number }] {
  return ['comments', { venueId }];
}

export function useVenueComments(venueId: number | null) {
  return useQuery({
    queryKey: venueCommentsQueryKey(venueId as number),
    queryFn: ({ signal }) => fetchVenueComments(venueId as number, signal),
    enabled: venueId !== null,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

export async function createVenueComment(input: VenueCommentCreate): Promise<CommentDto> {
  const envelope = await requestEnvelope<CommentDto>('POST', '/comments', ['data'], {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ venueId: input.venueId, body: input.body }),
  });
  return envelope.data;
}

/**
 * Negative ids for the rows this browser drew before the api answered — a real
 * `CommentDto.id` is a positive surrogate key, so the optimistic rows can never
 * collide with one (nor with each other).
 */
const OPTIMISTIC_ID_BASE = -1_000_000;
let optimisticSequence = 0;

/**
 * #16 with an OPTIMISTIC APPEND (the AC of T.C4a: "comments post appears
 * without reload"). The composer's row is on screen the moment the button is
 * clicked; a failure rolls the thread back to the snapshot taken in `onMutate`,
 * and every outcome revalidates once so the server's row (resolved author,
 * real id) replaces the optimistic one.
 */
export function useCreateVenueComment(venueId: number) {
  const client = useQueryClient();
  const key = venueCommentsQueryKey(venueId);

  return useMutation({
    mutationFn: (body: string) => createVenueComment({ venueId, body }),
    onMutate: async (body: string) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<CommentDto[]>(key);
      optimisticSequence += 1;
      const optimistic: CommentDto = {
        id: OPTIMISTIC_ID_BASE - optimisticSequence,
        venueId,
        pguId: null,
        authorUserId: 0,
        // The api resolves the display name on #16; until it answers, the row is
        // by construction THIS session's draft — `VenueComments` renders the
        // negative-id row as «Вы» and never mistakes it for someone else's.
        authorName: null,
        body,
        createdAt: new Date().toISOString(),
      };
      client.setQueryData<CommentDto[]>(key, [...(previous ?? []), optimistic]);
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context) client.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: key });
    },
  });
}
